import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  GovIssue,
  GovItemPage,
  GovKind,
  GovTask,
  getItems,
  governanceErrorMessage,
} from "../../../../utils/icGovernance";
import { statusesFor } from "../governanceStatus";
import { DEFAULT_SORT, MAX_Q, RegisterQuery, RegisterState, SORT_FIELDS } from "./useRegisterItems";

/**
 * The Master Project Tracker shows tasks AND issues in one list (user
 * decision 2026-10-12). IC_Ticket lists one kind per call, so this hook loads
 * `kind=TASK` and `kind=ISSUE` side by side with the same filters, merges the
 * rows and sorts them here. Filters live in the URL like `useRegisterItems`
 * (`?status=A,B&q=…&sort=…`) plus `?type=tasks|issues` (absent = both).
 */

export type TrackerRow = GovTask | GovIssue;
export type TrackerType = "all" | "tasks" | "issues";

const KIND_OF_TYPE: Record<TrackerType, GovKind[]> = {
  all: ["TASK", "ISSUE"],
  tasks: ["TASK"],
  issues: ["ISSUE"],
};

/** Task statuses first, then the issue-only ones (IN_PROGRESS is shared). */
export const TRACKER_STATUSES: string[] = Array.from(
  new Set([...statusesFor("TASK"), ...statusesFor("ISSUE")]),
);

const VALID_SORTS = new Set(SORT_FIELDS.flatMap((f) => [f.field, `-${f.field}`]));
const PRIORITY_RANK: Record<string, number> = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 };

/** The same order IC_Ticket applies per kind, over the merged rows. */
const comparator = (sort: string) => {
  const desc = sort.startsWith("-");
  const field = sort.replace(/^-/, "");
  const key = (r: TrackerRow): string | number => {
    switch (field) {
      case "priority":
        return PRIORITY_RANK[r.priority] ?? -1;
      case "title":
        return (r.title || "").toLowerCase();
      case "status":
        return r.status;
      case "updatedAt":
        return r.updatedAt || "";
      default:
        return r.createdAt || "";
    }
  };
  return (a: TrackerRow, b: TrackerRow) => {
    const ka = key(a);
    const kb = key(b);
    const c = ka < kb ? -1 : ka > kb ? 1 : 0;
    // Stable tiebreak so paging never reorders rows.
    return (desc ? -c : c) || a.id.localeCompare(b.id);
  };
};

type KindState = { rows: TrackerRow[]; cursor: string | null };
const EMPTY: KindState = { rows: [], cursor: null };

export interface TrackerState extends RegisterState<TrackerRow> {
  type: TrackerType;
  onTypeChange: (type: TrackerType) => void;
}

export const useTrackerItems = (): TrackerState => {
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = useMemo(() => {
    const allowed = new Set(TRACKER_STATUSES);
    return (searchParams.get("status") ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => allowed.has(s))
      .join(",");
  }, [searchParams]);
  const q = (searchParams.get("q") ?? "").trim().slice(0, MAX_Q).trim();
  const rawSort = searchParams.get("sort") ?? "";
  const sort = VALID_SORTS.has(rawSort) ? rawSort : DEFAULT_SORT;
  const rawType = searchParams.get("type");
  const type: TrackerType = rawType === "tasks" || rawType === "issues" ? rawType : "all";

  const query = useMemo<RegisterQuery>(
    () => ({ status: statusParam ? statusParam.split(",") : [], q, sort, mine: false, replyOnly: false }),
    [statusParam, q, sort],
  );

  const [byKind, setByKind] = useState<Record<GovKind, KindState>>({} as Record<GovKind, KindState>);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const kinds = KIND_OF_TYPE[type];

  /** The chosen statuses that this kind can have; null = the filter leaves this kind out. */
  const statusesForKind = useCallback(
    (kind: GovKind): string | null => {
      if (!statusParam) return "";
      const own = statusParam.split(",").filter((s) => statusesFor(kind).includes(s));
      return own.length ? own.join(",") : null;
    },
    [statusParam],
  );

  const fetchKind = useCallback(
    (kind: GovKind, cursor?: string | null): Promise<GovItemPage> => {
      const status = statusesForKind(kind);
      if (status === null) return Promise.resolve({ items: [], nextCursor: null });
      return getItems({ kind, status: status || undefined, q: q || undefined, sort, cursor });
    },
    [statusesForKind, q, sort],
  );

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    Promise.allSettled(kinds.map((kind) => fetchKind(kind)))
      .then((results) => {
        if (requestId !== requestIdRef.current) return;
        const next = {} as Record<GovKind, KindState>;
        let failure: unknown = null;
        results.forEach((result, i) => {
          if (result.status === "fulfilled") {
            next[kinds[i]] = {
              rows: (result.value.items ?? []) as TrackerRow[],
              cursor: result.value.nextCursor ?? null,
            };
          } else {
            console.error(`Failed to load ${kinds[i]} rows for the tracker:`, result.reason);
            next[kinds[i]] = EMPTY;
            failure = failure ?? result.reason;
          }
        });
        setByKind(next);
        if (failure) setError(governanceErrorMessage(failure));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
    // `kinds` follows `type`; listing `type` keeps the dependency array literal.
  }, [type, fetchKind]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const all = kinds.flatMap((kind) => byKind[kind]?.rows ?? []);
    return all.sort(comparator(sort));
  }, [byKind, kinds, sort]);

  const hasMore = kinds.some((kind) => !!byKind[kind]?.cursor);

  const onLoadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    const pending = kinds.filter((kind) => byKind[kind]?.cursor);
    Promise.allSettled(pending.map((kind) => fetchKind(kind, byKind[kind].cursor)))
      .then((results) => {
        if (requestId !== requestIdRef.current) return;
        setByKind((prev) => {
          const next = { ...prev };
          results.forEach((result, i) => {
            const kind = pending[i];
            if (result.status !== "fulfilled") {
              setError(governanceErrorMessage(result.reason));
              return;
            }
            const seen = new Set(prev[kind]?.rows.map((r) => r.id) ?? []);
            next[kind] = {
              rows: [
                ...(prev[kind]?.rows ?? []),
                ...((result.value.items ?? []) as TrackerRow[]).filter((r) => !seen.has(r.id)),
              ],
              cursor: result.value.nextCursor ?? null,
            };
          });
          return next;
        });
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoadingMore(false);
      });
  }, [hasMore, loadingMore, kinds, byKind, fetchKind]);

  const write = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          mutate(params);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onQueryChange = useCallback(
    (patch: Partial<RegisterQuery>) =>
      write((params) => {
        const set = (key: string, value: string, empty: string) => {
          if (!value || value === empty) params.delete(key);
          else params.set(key, value);
        };
        if (patch.status !== undefined) set("status", patch.status.join(","), "");
        if (patch.q !== undefined) set("q", patch.q.trim().slice(0, MAX_Q).trim(), "");
        if (patch.sort !== undefined) set("sort", patch.sort, DEFAULT_SORT);
      }),
    [write],
  );

  const onTypeChange = useCallback(
    (next: TrackerType) =>
      write((params) => {
        if (next === "all") params.delete("type");
        else params.set("type", next);
      }),
    [write],
  );

  return {
    rows,
    loading,
    loadingMore,
    error,
    // RegisterTable only reads this as "is there more"; the real cursors are per kind.
    nextCursor: hasMore ? "more" : null,
    onLoadMore,
    query,
    onQueryChange,
    type,
    onTypeChange,
  };
};
